<template>
  <div>
    <v-row>
      <v-col :lg="print ? 12 : 6" cols="12">
        <div class="border-card">
          <div class="text-h6 mb-2">Léčba</div>
          <div class="treatments-container">
            <v-timeline align-top dense>
              <v-timeline-item
                v-for="t in treatments"
                :key="t.id"
                :color="getDotColor(t)"
                small
                fill-dot
              >
                <div class="pr-12">
                  <div class="mb-1 grey--text text--darken-1">
                    Zahájena: {{ getCzechDateWithoutTime(t.startedOnLocal) }}
                  </div>
                  <div class="mb-1 grey--text text--darken-1">
                    {{
                      t.canceledAt
                        ? `Ukončena: ${getCzechDateWithoutTime(t.canceledAt)}`
                        : "Aktivní do teď"
                    }}
                  </div>

                  <div>
                    <div
                      class="dose-history-item pa-2 mb-2"
                      :style="{
                        borderLeftColor: getDosageColor(
                          t.doseSegments[0].dosageType,
                        ),
                      }"
                    >
                      <div class="mb-1 grey--text text--darken-1">
                        <small>Současná dávka</small>
                      </div>
                      <div>
                        Pero: {{ getDosageType(t.doseSegments[0].dosageType) }}
                      </div>
                      <div>Dávka: {{ t.doseSegments[0].doseMg }} mg</div>
                      <div>
                        Čas dávky:
                        {{ formatTime(t.doseSegments[0].scheduledTime) }}
                      </div>
                    </div>

                    <template v-if="t.doseSegments.length > 1">
                      <v-btn
                        text
                        small
                        class="px-0 dose-history-toggle"
                        @click="toggleHistory(t)"
                      >
                        <v-icon small class="mr-1">
                          {{
                            t.historyExpanded
                              ? "mdi-chevron-up"
                              : "mdi-chevron-down"
                          }}
                        </v-icon>
                        Historie dávek ({{ t.doseSegments.length - 1 }})
                      </v-btn>

                      <v-expand-transition>
                        <div v-if="t.historyExpanded" class="mt-2">
                          <div
                            v-for="(seg, si) in t.doseSegments.slice(1)"
                            :key="si"
                            class="dose-history-item pa-2 mb-2"
                            :style="{
                              borderLeftColor: getDosageColor(seg.dosageType),
                            }"
                          >
                            <div class="mb-1 grey--text text--darken-1">
                              <small
                                >Od
                                {{ getCzechDateWithoutTime(seg.from) }}</small
                              >
                            </div>
                            <div>Pero: {{ getDosageType(seg.dosageType) }}</div>
                            <div>Dávka: {{ seg.doseMg }} mg</div>
                            <div>
                              Čas dávky: {{ formatTime(seg.scheduledTime) }}
                            </div>
                          </div>
                        </div>
                      </v-expand-transition>
                    </template>
                  </div>
                </div>
              </v-timeline-item>
            </v-timeline>
          </div>
        </div>
      </v-col>
      <v-col v-show="!print" class="no-break">
        <div class="border-card">
          <div class="d-flex justify-space-between mb-4 align-center">
            <div>
              {{ calendarTitle }}
            </div>
            <div class="d-flex">
              <v-btn fab text small color="grey darken-2" @click="prev">
                <v-icon>mdi-chevron-left</v-icon>
              </v-btn>
              <v-btn fab text small color="grey darken-2" @click="next">
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
            </div>
          </div>
          <div class="loading-container">
            <v-progress-linear
              v-if="calendarLoading"
              indeterminate
              color="accent"
            ></v-progress-linear>
          </div>
          <v-calendar
            type="month"
            locale="cs-CZ"
            :weekdays="weekdays"
            v-model="calendarValue"
            ref="calendar"
            @change="updateTitle"
          >
            <template v-slot:day-label="{ day, date }">
              <div class="day-label-wrapper">
                <NorditropinStatusIcon :intake="getDayIntake(date)">
                  <v-icon
                    v-if="isDoctorVisitDay(date)"
                    size="20"
                    color="primary"
                    >mdi-bell-outline</v-icon
                  >
                  <template v-else>{{ day }}</template>
                </NorditropinStatusIcon>
              </div>
            </template>
          </v-calendar>
        </div>

        <div class="mt-4 border-card">
          <div class="title mb-4">Legenda</div>

          <div
            class="d-flex align-center mb-2"
            v-for="(l, i) in legend"
            :key="i"
          >
            <NorditropinStatusIcon :intake="l" :legend="true" class="mr-2">
            </NorditropinStatusIcon>
            <div v-html="l.text"></div>
          </div>

          <div class="d-flex align-center">
            <NorditropinStatusIcon :intake="null" :legend="true" class="mr-2">
            </NorditropinStatusIcon>
            <div>
              <div>Chybí záznam o přípravku Norditropin<sup>®</sup></div>
            </div>
          </div>

          <div
            class="d-flex align-center mt-2"
            v-if="patient && patient.checkupWithDoctorAtLocal"
          >
            <div class="doctor-legend-icon mr-2">
              <v-icon size="32" color="primary">mdi-bell-outline</v-icon>
            </div>
            <div>Plánovaná návštěva lékaře</div>
          </div>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import NorditropinStatusIcon from "@/components/icons/NorditropinStatusIcon.vue";
import useApiCall from "@/use/apiCall";
import useFormatDate from "@/use/formatDate";

export default {
  components: {
    NorditropinStatusIcon,
  },

  setup() {
    const { getData } = useApiCall();
    const { getCzechDate, getDateToSend, getCzechDateWithoutTime } =
      useFormatDate();

    return {
      getData,
      getCzechDate,
      getCzechDateWithoutTime,
      getDateToSend,
    };
  },

  props: {
    patient: {
      type: Object,
      required: true,
    },
    print: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      calendarValue: this.getStartOfMonth(new Date()),
      weekdays: [1, 2, 3, 4, 5, 6, 0],
      calendarTitle: "",
      treatments: [],
      intakes: [],
      calendarLoading: false,
      legend: [
        {
          status: "accepted",
          dosageType: "norditropin5Mg",
          text: "Norditropin<sup>®</sup> 5 mg byl vzat",
        },
        {
          status: "accepted",
          dosageType: "norditropin10Mg",
          text: "Norditropin<sup>®</sup> 10 mg byl vzat",
        },
        {
          status: "accepted",
          dosageType: "norditropin15Mg",
          text: "Norditropin<sup>®</sup> 15 mg byl vzat",
        },
        {
          status: "rejected",
          dosageType: "norditropin15Mg",
          text: "Přípravek Norditropin<sup>®</sup> nebyl aplikován",
        },
      ],
    };
  },

  async mounted() {
    this.updateTitle();
    await this.fetchTreatment();
    await this.fetchTreatmentIntakes();
    this.$emit("loaded");
  },

  methods: {
    async fetchTreatment() {
      const patientId = Number(this.$route.params.id);

      const params = {
        patientId: patientId,
        orderBy: "createdAtDesc",
      };

      const { treatments } = await this.getData("norditropin/treatments", {
        params,
      });

      this.treatments = await Promise.all(
        treatments.map((t) => this.attachDoseSegments(t)),
      );
    },

    async attachDoseSegments(treatment) {
      const history = await this.getData(
        `norditropin/treatments/${treatment.id}/history`,
        { params: { orderBy: "createdAtAsc" } },
      );
      const treatmentHistory = history?.treatmentHistory ?? [];

      const doseSegments = [
        {
          from: treatment.startedOnLocal,
          dosageType:
            treatmentHistory[0]?.oldDosageType ?? treatment.dosageType,
          doseMg: treatmentHistory[0]?.oldDoseMg ?? treatment.doseMg,
          scheduledTime:
            treatmentHistory[0]?.oldTakeMedicineScheduledTime ??
            treatment.takeMedicineScheduledTime,
        },
        ...treatmentHistory.map((h) => ({
          from: h.createdAt,
          dosageType: h.newDosageType,
          doseMg: h.newDoseMg,
          scheduledTime: h.newTakeMedicineScheduledTime,
        })),
      ].reverse();

      return { ...treatment, doseSegments, historyExpanded: this.print };
    },

    toggleHistory(treatment) {
      treatment.historyExpanded = !treatment.historyExpanded;
    },

    async fetchTreatmentIntakes() {
      this.intakes = [];
      this.calendarLoading = true;
      const patientId = Number(this.$route.params.id);

      const params = {
        patientId: patientId,
        from: this.getDateToSend(this.getStartOfMonth(this.calendarValue)),
        end: this.getDateToSend(this.getEndOfMonth(this.calendarValue)),
      };

      const { intakes } = await this.getData("norditropin/treatments/intakes", {
        params,
      });

      this.intakes = intakes;
      this.calendarLoading = false;
    },

    formatTime(time) {
      return time ? time.slice(0, 5) : "-";
    },

    getDosageType(dosageType) {
      if (dosageType === "norditropin5Mg") {
        return "5 mg";
      } else if (dosageType === "norditropin10Mg") {
        return "10 mg";
      } else if (dosageType === "norditropin15Mg") {
        return "15 mg";
      } else {
        return "-";
      }
    },

    getDosageColor(dosageType) {
      if (dosageType === "norditropin5Mg") {
        return "#f9a333";
      } else if (dosageType === "norditropin10Mg") {
        return "#34b2e3";
      } else if (dosageType === "norditropin15Mg") {
        return "#9bc05c";
      } else {
        return "grey";
      }
    },

    getDotColor(treatment) {
      return this.getDosageColor(treatment.dosageType);
    },

    isDoctorVisitDay(date) {
      if (!this.patient?.checkupWithDoctorAtLocal) return false;
      const ensureLeadingZeros = (dateString) => {
        const [year, month, day] = dateString.split("-");
        return `${year}-${String(month).padStart(2, "0")}-${String(
          day,
        ).padStart(2, "0")}`;
      };
      const visitDate = this.getDateToSend(
        this.patient.checkupWithDoctorAtLocal,
      );
      return visitDate === ensureLeadingZeros(date);
    },

    getDayIntake(date) {
      const intake = this.intakes.find((i) => {
        const formattedDate = this.getDateToSend(i.createdAtLocal);
        if (formattedDate === date) {
          return i;
        }
      });

      return intake;
    },

    getStartOfMonth(date) {
      return new Date(date.getFullYear(), date.getMonth(), 1);
    },

    getEndOfMonth(date) {
      return new Date(date.getFullYear(), date.getMonth() + 1, 0);
    },

    async prev() {
      this.calendarValue = this.getStartOfMonth(
        new Date(
          this.calendarValue.getFullYear(),
          this.calendarValue.getMonth() - 1,
        ),
      );
      await this.fetchTreatmentIntakes();
    },
    async next() {
      this.calendarValue = this.getStartOfMonth(
        new Date(
          this.calendarValue.getFullYear(),
          this.calendarValue.getMonth() + 1,
        ),
      );
      await this.fetchTreatmentIntakes();
    },
    updateTitle() {
      this.calendarTitle = this.$refs.calendar.title || "";
    },
  },
};
</script>

<style lang="scss" scoped>
.loading-container {
  height: 4px;
}

.dose-history-toggle {
  text-transform: none;
  letter-spacing: normal;
}

.dose-history-item {
  border-radius: 8px;
  background-color: #f5f5f5;
  border-left: 3px solid #bdbdbd;
}

::v-deep .v-timeline-item__dot--small {
  box-shadow: none;
  width: 12px;
  height: 12px;
}

.day-label-wrapper {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
}

.doctor-icon {
  position: absolute;
  bottom: 2px;
  right: 2px;
}

.doctor-legend-icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
}

::v-deep.v-calendar {
  .v-outside {
    .status {
      background-color: transparent;
      border: none;
    }
  }

  .v-calendar-weekly__day-label {
    margin: auto;
    display: flex;
    width: 100%;
    height: 4rem;
    justify-content: center;
    align-items: center;
  }
}

@media print {
  .no-break,
  .dose-history-item {
    page-break-inside: avoid; /* Avoid breaking inside the element */
    break-inside: avoid; /* Ensures compatibility with modern browsers */
    display: block;
  }
}
</style>
